/** Home: setup progress until the company is live, then what needs attention. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Circle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { amount, Decimal } from '../lib/money';
import { shortDate } from '../lib/format';
import { Alert, Button, Card, Money, PageHeader, Skeleton, StatusChip } from '../components/ui';
import { companyQuery, type CompanyView } from './setup/CompanyPage';
import { KPI_STYLE, Kpi, TrendCharts, useTrends } from './DashboardCharts';

const STEPS: { key: string; title: string; to: string; hint: string }[] = [
  { key: 'company', title: 'Company & BIR details', to: '/setup/company', hint: 'TIN, RDO, registered address, VAT status.' },
  { key: 'branches', title: 'Branches & invoice series', to: '/setup/branches', hint: 'Every BIR-registered branch and its serial ranges.' },
  { key: 'coa', title: 'Chart of accounts', to: '/accounts', hint: 'Review the template; rename or add accounts.' },
  { key: 'tax', title: 'Tax setup', to: '/setup/company', hint: 'Taxpayer class and VAT or percentage tax.' },
  { key: 'opening', title: 'Opening balances', to: '/opening-balances', hint: 'Balances on the day you start using CoreX-lite.' },
  { key: 'users', title: 'Invite your team', to: '/setup/users', hint: 'Bookkeepers, approvers, your accountant.' },
];

function Setup({ company }: { company: CompanyView }): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const done = new Set(company.setupSteps);
  const mark = useMutation({
    mutationFn: (step: string) => api<CompanyView>('/company/setup/complete-step', { method: 'POST', body: { step } }),
    onSuccess: (data) => queryClient.setQueryData(['company'], data),
  });
  const next = STEPS.find((s) => !done.has(s.key));
  return (
    <Card title="Get set up" description={`${done.size} of ${STEPS.length} done. Setup to first invoice takes about 30 minutes.`}>
      {mark.isError && <div className="mb-3"><Alert tone="critical">{(mark.error as Error).message}</Alert></div>}
      <ol className="divide-y divide-slate-100 dark:divide-slate-800">
        {STEPS.map((s) => {
          const isDone = done.has(s.key);
          return (
            <li key={s.key} className="flex flex-wrap items-center gap-3 py-3">
              {isDone ? <CheckCircle2 aria-label="Done" className="h-5 w-5 text-emerald-600" /> : <Circle aria-label="To do" className="h-5 w-5 text-slate-300" />}
              <div className="min-w-0 flex-1">
                <Link to={s.to} className="font-medium hover:underline">{s.title}</Link>
                <p className="text-sm text-slate-500">{s.hint}</p>
              </div>
              {!isDone && can('company.manage') && (
                <Button size="sm" variant={next?.key === s.key ? 'primary' : 'secondary'} busy={mark.isPending && mark.variables === s.key} onClick={() => mark.mutate(s.key)}>
                  Mark done
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

interface Owner {
  cash: { accounts: { accountId: string; name: string; balance: string }[]; total: string };
  receivables: { total: string; overdue: string; count: number };
  payables: { total: string; dueThisWeek: string; overdue: string };
  profit: { thisMonth: { revenue: string; netIncome: string }; lastMonth: { revenue: string; netIncome: string } };
  tax: { upcoming: { form: string; label: string; periodLabel: string; dueDate: string; status: string; overdue: boolean }[]; estimates: { form: string; period: string; taxDue: string }[] };
}
interface Accountant { unreconciledLines: number; awaitingApproval: { journals: number; bills: number; payments: number }; missing2307: number; draftInvoices: number; validation: { errors: number; warnings: number } }
interface AlertItem { kind: string; severity: 'critical' | 'warning' | 'info'; message: string; link: string }

function Attention(): JSX.Element {
  const { can } = useAuth();
  const owner = useQuery({ queryKey: ['dash-owner'], queryFn: () => api<Owner>('/dashboard/owner'), enabled: can('dashboard.view') && can('reports.view', 'bank.view') });
  const acct = useQuery({ queryKey: ['dash-acct'], queryFn: () => api<Accountant>('/dashboard/accountant'), enabled: can('journals.view', 'bills.view', 'tax.view') });
  const alerts = useQuery({ queryKey: ['alerts'], queryFn: () => api<AlertItem[]>('/alerts'), enabled: can('dashboard.view') });
  const trends = useTrends(can('reports.view'));
  const [showAll, setShowAll] = useState(false);
  const o = owner.data;
  const a = acct.data;
  const delta = o ? new Decimal(o.profit.thisMonth.netIncome).minus(o.profit.lastMonth.netIncome) : null;
  return (
    <div className="space-y-4">
      {owner.isLoading && <Skeleton className="h-28 w-full" />}
      {o && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi {...KPI_STYLE.cash} label="Cash across all accounts" value={amount(o.cash.total)} sub={o.cash.accounts.filter((c) => !new Decimal(c.balance).isZero()).slice(0, 2).map((c) => `${c.name} ${amount(c.balance)}`).join(' · ')} to="/banking" />
          <Kpi {...KPI_STYLE.ar} label="Customers owe" value={amount(o.receivables.total)} sub={`${amount(o.receivables.overdue)} overdue · ${o.receivables.count} invoices`} to="/reports/aged-receivables" />
          <Kpi {...KPI_STYLE.ap} label="You owe suppliers" value={amount(o.payables.total)} sub={`${amount(o.payables.dueThisWeek)} due within a week`} to="/reports/cash-requirements" />
          <Kpi {...KPI_STYLE.profit} label="Net income this month" value={amount(o.profit.thisMonth.netIncome)} trend={delta ? (delta.isNegative() ? 'down' : 'up') : undefined} sub={delta ? `${delta.isNegative() ? '' : '+'}${amount(delta.toString())} vs last month` : undefined} to="/reports/statements/sci" />
        </div>
      )}
      {can('reports.view') && <TrendCharts data={trends.data} />}
      {alerts.data && alerts.data.length > 0 && (
        <Card title="Needs attention">
          <ul className="space-y-2 text-sm">
            {(showAll ? alerts.data : alerts.data.slice(0, 5)).map((x, i) => (
              <li key={i} className="flex items-start gap-2">
                <StatusChip tone={x.severity === 'critical' ? 'critical' : x.severity === 'warning' ? 'warning' : 'info'}>{x.severity === 'critical' ? 'Urgent' : x.severity === 'warning' ? 'Soon' : 'Note'}</StatusChip>
                <Link className="hover:underline" to={x.link}>{x.message}</Link>
              </li>
            ))}
          </ul>
          {alerts.data.length > 5 && <button className="mt-3 text-sm font-medium text-brand-700 hover:underline dark:text-brand-100" onClick={() => setShowAll(!showAll)}>{showAll ? 'Show fewer' : `Show all ${alerts.data.length}`}</button>}
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        {o && (
          <Card title="BIR deadlines" description="Estimates are this quarter's figures so far.">
            <ul className="mb-3 space-y-1 text-sm">{o.tax.estimates.map((e) => <li key={e.form} className="flex justify-between"><span>{e.form.replace(/^F/, '')} {e.period} estimate</span><Money className="font-semibold">{amount(e.taxDue)}</Money></li>)}</ul>
            <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {o.tax.upcoming.map((c) => <li key={`${c.form}-${c.periodLabel}`} className="flex justify-between gap-3 py-1.5"><Link to="/tax" className="hover:underline">{c.label.split(' — ')[0]} · {c.periodLabel}</Link><span className={c.overdue ? 'font-semibold text-red-600' : 'text-slate-500'}>{c.overdue ? 'overdue' : shortDate(c.dueDate)}</span></li>)}
            </ul>
          </Card>
        )}
        {a && (
          <Card title="Accountant's list">
            <ul className="space-y-2 text-sm">
              <li className="flex justify-between"><Link className="hover:underline" to="/banking">Bank lines to reconcile</Link><span className="font-semibold tabular-nums">{a.unreconciledLines}</span></li>
              <li className="flex justify-between"><Link className="hover:underline" to="/journals?status=PENDING_APPROVAL">Awaiting approval (journals / bills / payments)</Link><span className="font-semibold tabular-nums">{a.awaitingApproval.journals} / {a.awaitingApproval.bills} / {a.awaitingApproval.payments}</span></li>
              <li className="flex justify-between"><Link className="hover:underline" to="/receipts/2307-tracker">Customer 2307s not yet received</Link><span className="font-semibold tabular-nums">{a.missing2307}</span></li>
              <li className="flex justify-between"><Link className="hover:underline" to="/sales/invoices">Draft invoices</Link><span className="font-semibold tabular-nums">{a.draftInvoices}</span></li>
              <li className="flex justify-between"><Link className="hover:underline" to="/tax/checks">Pre-filing issues this quarter</Link><span className={`font-semibold tabular-nums ${a.validation.errors ? 'text-red-600' : ''}`}>{a.validation.errors} errors, {a.validation.warnings} warnings</span></li>
            </ul>
          </Card>
        )}
      </div>
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const { user, can } = useAuth();
  const company = useQuery({ ...companyQuery, enabled: Boolean(user?.companyId) && can('company.view', 'company.manage') });

  if (!user?.companyId) {
    return (
      <>
        <PageHeader title={`Welcome, ${user?.name ?? ''}`} />
        <Card>
          {user?.isPlatformAdmin ? (
            <p className="text-sm">You are not a member of any company. Create one under <Link className="text-brand-700 underline dark:text-brand-100" to="/platform/companies">Client Companies</Link>.</p>
          ) : (
            <p className="text-sm">You do not have access to any company yet. Ask your company's administrator to invite you.</p>
          )}
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title={user.companyName ?? 'Dashboard'} description={`Signed in as ${user.roleName}`} />
      <div className="space-y-4">
        {company.data && company.data.status === 'SETUP' && <Setup company={company.data} />}
        <Attention />
      </div>
    </>
  );
}
